import ErrorSummary from '@root/core/src/components/error-summary';
import PropTypes from '@root/vendor/prop-types';
import ProtectedRouter from '@root/agents.joinroot.com/src/components/routers/protected-router';
import React, { useCallback, useEffect, useRef, useState } from '@root/vendor/react';
import SceneLoader from '@root/core/src/components/scene-loader';
import useAnalytics from '@root/core/src/hooks/use-analytics';
import { createBridgeLinkWithExperienceBasedAttribution } from '@root/agents.joinroot.com/src/api/create-agency-platform-bridge-link';
import { datadogRum } from '@datadog/browser-rum';
import { useAgentState } from '@root/agents.joinroot.com/src/contexts/agent-context';
import { useHistory } from '@root/vendor/react-router-dom';
import { useLogger } from '@root/agents.joinroot.com/src/utils/logging';
import { validateSessionToken } from '@root/agents.joinroot.com/src/api/root-server';

export default function ProtectedApp({
  onLogout,
  pingInterval = 10000,
  redirectOnLoginPath,
  quoteIdForRedirect,
  quoteSourceForRedirect,
  quoteExperienceForRedirect,
}) {
  const isMounted = useRef(false);
  const history = useHistory();
  const agent = useAgentState();
  const logger = useLogger();
  const { trackEvent } = useAnalytics('AUTHENTICATED_APP');

  const [redirectState, setRedirectState] = useState({
    isRedirecting: false,
    hasError: false,
    shouldShowRouter: true,
  });

  const performRedirect = useCallback(async () => {
    logger.info('Attempting to generate redirect link to agency platform');
    trackEvent('ATTEMPT_REDIRECT_TO_AGENCY_PLATFORM', {
      quoteId: quoteIdForRedirect,
      quoteSource: quoteSourceForRedirect,
    });

    try {
      setRedirectState({
        isRedirecting: true,
        hasError: false,
        shouldShowRouter: false,
      });

      const bridgeUrl = await createBridgeLinkWithExperienceBasedAttribution(
        quoteIdForRedirect,
        agent.id,
        quoteSourceForRedirect,
        quoteExperienceForRedirect
      );
      logger.info('Successfully generated redirect link to agency platform');

      // Stop DD RUM session before bridging to HostedX for agency_platform agents
      if (agent.agency?.quotingExperience === 'agency_platform') {
        if (typeof datadogRum !== 'undefined' && datadogRum.stopSession) {
          datadogRum.stopSession();
          logger.info('Stopped DD RUM session before bridging to HostedX');
        }

        // Clear DataDog cookies to prevent session continuity
        // This is especially important in localhost where both apps share the same domain
        const ddCookies = ['_dd_s', '_dd', '_dd_l', '_dd_r'];
        ddCookies.forEach((cookieName) => {
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          // Also try domain-scoped deletion for localhost
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=localhost`;
        });

        // Set flag so app-entry knows to suppress DD RUM if user navigates back
        sessionStorage.setItem('hostedx_bridge_in_progress', 'true');
      }

      trackEvent('SUCCESS_REDIRECT_TO_AGENCY_PLATFORM', {
        quoteId: quoteIdForRedirect,
        quoteSource: quoteSourceForRedirect,
      });
      window.open(bridgeUrl, '_self');
    } catch (error) {
      logger.error('Failed to generate redirect link to agency platform', error);
      trackEvent('FAILED_REDIRECT_TO_AGENCY_PLATFORM', {
        quoteId: quoteIdForRedirect,
        quoteSource: quoteSourceForRedirect,
      });
      setRedirectState({
        isRedirecting: false,
        hasError: true,
        shouldShowRouter: false,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteIdForRedirect, quoteSourceForRedirect, quoteExperienceForRedirect, agent?.id]);

  useEffect(() => {
    // Handle external redirect if quote_id is present
    const trimmedPath = history.location.pathname.replace(/\/$/, '');
    if (trimmedPath !== '/authenticate' && quoteIdForRedirect && agent?.id) {
      performRedirect();
      return; // Don't execute normal redirect logic
    }

    // Normal redirect logic
    if (redirectOnLoginPath && !isMounted.current) {
      history.replace(redirectOnLoginPath);
    }

    isMounted.current = true;
    return () => isMounted.current = false;
  }, [redirectOnLoginPath, quoteIdForRedirect, quoteSourceForRedirect, agent?.id, history, performRedirect]);

  useEffect(() => {
    const pingForToken = async () => {
      try {
        const data = await validateSessionToken();

        if (!data.valid && isMounted.current) {
          onLogout();
        }
      } catch (e) {
        // If there is a bad network connection `fetch` will throw a TypeError
        // when trying to make a request. We want to trap this error here since
        // we dont want to show the error page when there is a bad connection.
        if (!(e instanceof TypeError)) {
          throw e;
        }
      }
    };

    const interval = setInterval(pingForToken, pingInterval);
    return () => clearInterval(interval);
  }, [onLogout, pingInterval]);

  if (redirectState.isRedirecting) {
    return <SceneLoader />;
  }

  if (redirectState.hasError) {
    return (
      <ErrorSummary
        heading={'Failed to redirect to quote'}
        message={'Please try again later or contact support if the issue persists.'}
      />
    );
  }

  return <ProtectedRouter onLogout={onLogout} />;
}

ProtectedApp.propTypes = {
  onLogout: PropTypes.func.isRequired,
  pingInterval: PropTypes.number,
  quoteExperienceForRedirect: PropTypes.string,
  quoteIdForRedirect: PropTypes.string,
  quoteSourceForRedirect: PropTypes.string,
  redirectOnLoginPath: PropTypes.string,
};
